import { useQuery } from '@tanstack/react-query';

import API from '@api/API';

export interface BatchReconciliationActionCorrection {
  type: 'reject' | 'removeDuplicate' | 'mergedInto' | 'duplicateAdded';
  reason: string;
  correctedAt: string;
  eventId?: string;
  targetActionId?: string;
  fromActionId?: string;
}

export interface BatchReconciliationAction {
  _id: string;
  status: 'pending' | 'approved' | 'rejected' | 'superseded';
  type: 'merge' | 'auto-approve';
  executedAt?: string;
  cityIds: string[];
  startDate: string;
  primaryEventId: string;
  duplicateEventIds: string[];
  matchType?: 'deterministic' | 'llm';
  approvedPrimary?: boolean;
  createdAt: string;
  metadata: {
    eventStatusesById: Record<string, string>;
    cities: { _id: string; name: string }[];
    seriesOptions: { _id: string; title: string }[];
    hydratedEvents: Record<string, any>;
    sourceLogsByEventId: Record<string, any>;
    entertainersGroupedByEventId: Record<string, any>;
  };
  review?: {
    reviewedAt: string;
    corrections: BatchReconciliationActionCorrection[];
    resolution?: {
      titleFromEventId: string;
      imageFromEventId: string;
      venueName: string;
      claimedEntertainerIds: string[];
    };
  };
}

export const useBatchReconciliationActionsQuery = (
  {
    filter,
    startDate,
  }: {
    filter?: string;
    startDate?: string;
  },
  options?: {
    isEnabled?: boolean;
  },
) => {
  const { isEnabled = true } = options || {};
  const query = new URLSearchParams();

  if (filter) {
    query.set('filter', filter);
  }

  const { isFetched, data, refetch } = useQuery({
    queryKey: useBatchReconciliationActionsQuery.queryKey(filter, startDate),
    queryFn: () => useBatchReconciliationActionsQuery.queryFn(filter, startDate),
    enabled: isEnabled,
  });

  return {
    batchReconciliationActionsAreReady: isFetched,
    batchReconciliationActions: data?.batchReconciliationActions,
    batchReconciliationActionDates: data?.meta.dates,
    batchReconciliationActionTotal: data?.meta.total,
    refetchBatchReconciliationActions: refetch,
  };
};
useBatchReconciliationActionsQuery.queryKey = (filter?: string, startDate?: string) => [
  'batchReconciliationActions',
  filter,
  startDate,
];

useBatchReconciliationActionsQuery.queryFn = (filter?: string, startDate?: string) => {
  const queryParams = new URLSearchParams();

  if (filter) {
    queryParams.set('filter', filter);
  }

  if (startDate) {
    queryParams.set('startDate', startDate);
  }

  return API.get<{
    batchReconciliationActions: BatchReconciliationAction[];
    meta: { dates: string[]; total: number };
  }>(`/v1/super-admin/batch-reconciliation-actions?${queryParams.toString()}`);
};
